import { FunctionComponent } from 'react';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import { TooltipItem, ChartType } from 'chart.js';

export type GroupedBarChartSeries = {
  label: string;
  values: number[];
  color: string;
};

export type GroupedBarChartProps = {
  labels: string[];
  series: GroupedBarChartSeries[];
  formatValue?: (value: number) => string;
  // Extra tooltip lines below the per-series rows (e.g. net / percentage summaries)
  getTooltipFooter?: (groupIndex: number) => string[];
  height?: string;
};

type FC = FunctionComponent<GroupedBarChartProps>;

const GroupedBarChart: FC = ({
  labels,
  series,
  formatValue = (value: number) => value.toLocaleString(),
  getTooltipFooter,
  height = '260px',
}: GroupedBarChartProps) => {
  return (
    <div style={{ height, position: 'relative' }} data-testid="grouped-bar-chart">
      <Bar
        data={{
          labels,
          datasets: series.map((s) => ({
            label: s.label,
            data: s.values,
            backgroundColor: s.color,
            borderRadius: 4,
            borderSkipped: 'start' as const,
            categoryPercentage: 0.45,
            barPercentage: 0.8,
          })),
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index' as const,
            intersect: false,
          },
          scales: {
            x: {
              ticks: {
                color: '#5E5E5E',
              },
              grid: {
                display: false,
              },
            },
            y: {
              // Auto-scaled so the axis adapts when the date filter changes the data
              beginAtZero: true,
              ticks: {
                maxTicksLimit: 6,
                color: '#5E5E5E',
                callback: (value: string | number) => formatValue(Number(value)),
              },
              grid: {
                display: true,
                color: '#EFEFEF',
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                title(context: TooltipItem<ChartType>[]) {
                  return context[0]?.label ?? '';
                },
                label(context: TooltipItem<ChartType>) {
                  return `${context.dataset.label}: ${formatValue(Number(context.raw))}`;
                },
                footer(context: TooltipItem<ChartType>[]) {
                  const groupIndex = context[0]?.dataIndex;
                  if (groupIndex === undefined || !getTooltipFooter) {
                    return '';
                  }
                  return getTooltipFooter(groupIndex);
                },
              },
              padding: 10,
              backgroundColor: '#FBFBFB',
              titleColor: '#2D2D2D',
              bodyColor: '#2D2D2D',
              footerColor: '#008107',
              displayColors: true,
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              caretSize: 6,
              borderColor: '#EFEFEF',
              borderWidth: 1,
              cornerRadius: 8,
            },
          },
        }}
      />
    </div>
  );
};

export default GroupedBarChart;
